/**
 * Enable/disable analytics and tracking services based on cookie consent.
 * Services: Google Tag Manager (GTM), LiveSession, PostHog.
 * Opt-out: all services run by default until the user chooses "Necessary only" or disables analytics.
 */

import { liveSessionSdk as ls } from 'src/utils/liveSessionSdk';
import posthog from 'posthog-js';
import TagManager from 'react-gtm-module';
import * as CookieConsent from 'vanilla-cookieconsent';

import type { AnalyticsConsentState } from 'src/store';

const GTM_ID = 'GTM-5RD9PGN';
const LIVESESSION_PROJECT_ID = '142cc529.3e52a220';

/** When true, services are enabled until user explicitly opts out. */
const OPT_OUT_MODE = true;

let gtmInitialized = false;
let liveSessionInitialized = false;

type CookieConsentAPI = {
  validConsent?: () => boolean;
  getUserPreferences?: () => {
    acceptedCategories: string[];
    acceptedServices: Record<string, string[]>;
  };
};

function getConsentState(): {
  analyticsAccepted: boolean;
  gtm: boolean;
  liveSession: boolean;
  posthog: boolean;
} {
  const api = CookieConsent as unknown as CookieConsentAPI;
  const hasValidConsent = api.validConsent?.() ?? false;

  // Opt-out: no valid consent yet => treat as all accepted
  if (OPT_OUT_MODE && !hasValidConsent) {
    return {
      analyticsAccepted: true,
      gtm: true,
      liveSession: true,
      posthog: true,
    };
  }

  const prefs = api.getUserPreferences?.();
  if (!prefs) {
    return {
      analyticsAccepted: false,
      gtm: false,
      liveSession: false,
      posthog: false,
    };
  }

  const analyticsAccepted = prefs.acceptedCategories?.includes('analytics') ?? false;
  const accepted = prefs.acceptedServices?.analytics ?? [];

  // Plugin may store service by config key (gtm) or by label (Google Tag Manager)
  const hasService = (key: string, label: string) =>
    accepted.includes(key) || accepted.includes(label);

  return {
    analyticsAccepted,
    gtm: analyticsAccepted && hasService('gtm', 'Google Tag Manager'),
    liveSession: analyticsAccepted && hasService('liveSession', 'LiveSession'),
    posthog: analyticsAccepted && hasService('posthog', 'PostHog'),
  };
}

export function enableGtm(): void {
  if (gtmInitialized) return;
  try {
    TagManager.initialize({ gtmId: GTM_ID });
    gtmInitialized = true;
  } catch (e) {
    console.warn('GTM init failed:', e);
  }
}

export function disableGtm(): void {
  // GTM does not support runtime teardown; we only avoid initializing until consent.
  // Once enabled, it stays enabled until page reload.
  gtmInitialized = true; // prevent re-init if they accept again
}

export function enablePosthog(): void {
  try {
    posthog.opt_in_capturing();
  } catch (e) {
    console.warn('PostHog opt-in failed:', e);
  }
}

export function disablePosthog(): void {
  try {
    posthog.opt_out_capturing();
  } catch (e) {
    console.warn('PostHog opt-out failed:', e);
  }
}

/** Call at app bootstrap so LiveSession is ready before any component uses it. Idempotent. */
export function ensureLiveSessionInited(): void {
  if (liveSessionInitialized) return;
  try {
    ls.init(LIVESESSION_PROJECT_ID, { keystrokes: true });
    liveSessionInitialized = true;
  } catch (e) {
    console.warn('LiveSession init failed:', e);
  }
}

export function enableLiveSession(): void {
  ensureLiveSessionInited();
}

export function disableLiveSession(): void {
  // LiveSession SDK does not expose a destroy/disable; we only init when consent is given.
  liveSessionInitialized = true; // prevent re-init if they accept again
}

/**
 * Read current consent from CookieConsent and enable/disable each service.
 * Uses validConsent() and getUserPreferences() so consent actually reflects user choices.
 * Opt-out: when validConsent() is false, all analytics services are enabled.
 * Call from onConsent, onChange, and after CookieConsent.run() has resolved.
 */
export function applyConsentToServices(
  setAnalyticsConsent: (state: AnalyticsConsentState) => void,
): void {
  const { gtm, liveSession, posthog } = getConsentState();

  const state: AnalyticsConsentState = { gtm, liveSession, posthog };

  if (state.gtm) enableGtm();
  if (state.liveSession) enableLiveSession();
  if (state.posthog) {
    enablePosthog();
  } else {
    disablePosthog();
  }

  setAnalyticsConsent(state);
}
